import React, { useState, useEffect, useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { Code, ConnectError } from "@connectrpc/connect";
import { QRCodeSVG } from "qrcode.react";
import { OrgContext } from "contexts/OrgContext";
import { UserContext } from "contexts/UserContext";
import {
  ArrowLeft,
  Copy,
  MoreHorizontal,
  Pencil,
  Plus,
  QrCode,
} from "lucide-react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import { HttpPortProtocol } from "@buf/brevdev_devplane.bufbuild_es/devplaneapi/v1/network_pb";
import { Button } from "components/Graphs/Button";
import { Input } from "components/Graphs/Input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/Graphs/Dialogue";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "components/Graphs/Dropdown";
import {
  NodeDetailIconAction,
  NodeDetailSectionDescription,
  NodeDetailSectionHeader,
  NodeDetailTable,
  NodeDetailTableBody,
  NodeDetailTableCell,
  NodeDetailTableHead,
  NodeDetailTableHeaderCell,
} from "components/NodeDetail/shared/NodeDetailSection";
import {
  AllowedSourcesEditor,
  AllowedSourcesValue,
  CopyButton,
  ConfirmDialog,
  getVisibleOverflowItems,
  NetworkTableLoadingRow,
  NeutralInlinePill,
  ScrollableNetworkDialogBody,
  ScrollableNetworkDialogContent,
} from "./shared";
import type { OpenHTTPPortOptions } from "./useNodeDetail";
import { validateTunnelName } from "components/utils";
import { isOrgAdminOrMaintainerRole } from "utils/roleUtils";
import type { DisplayPort } from "utils/environment/networkAccessFallback";
import { MicroK8sNodePortNotice } from "components/Environment/shared/MicroK8sNodePortNotice";
import { DisabledActionTooltip } from "components/Environment/shared/DisabledActionTooltip";
import {
  getCurrentUserAccessIdentities,
  userCanAccessSecureLink,
} from "components/Environment/utils/secureLinkAccess";
import agent from "server";
import { toast } from "utils/toast";

const AUTH_ID_EMAIL_QUERY_STALE_TIME_MS = 5 * 60 * 1000;
const MAX_AUTHORIZED_IDENTITIES_IN_ROW = 4;
const MAX_ALLOWED_SOURCES_IN_ROW = 3;
const SECURE_LINK_NAME_CONFLICT_MESSAGE =
  "That secure link name is already in use. Use a unique name.";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function isSecureLinkNameConflictError(error: unknown): boolean {
  if (error instanceof ConnectError) {
    return error.code === Code.AlreadyExists;
  }

  if (!isRecord(error)) return false;

  const code = error.code;
  const response = error.response;
  return (
    code === Code.AlreadyExists ||
    code === "already_exists" ||
    code === "ALREADY_EXISTS" ||
    error.status === 409 ||
    error.statusCode === 409 ||
    (isRecord(response) &&
      (response.status === 409 || response.statusCode === 409))
  );
}

function httpProtocolLabel(protocol: HttpPortProtocol): string {
  return protocol === HttpPortProtocol.HTTPS ? "HTTPS" : "HTTP";
}

function stringArrayEquals(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((value, index) => value === b[index]);
}

function isEmailIdentity(identity: string): boolean {
  return identity.includes("@");
}

function AuthorizedIdentityText({ identity }: { identity: string }) {
  const { me } = useContext(UserContext);
  const trimmedIdentity = identity.trim();
  const currentUserEmail =
    me?.externalAuthId === trimmedIdentity ? me.email : undefined;
  const shouldResolve =
    trimmedIdentity.length > 0 &&
    !isEmailIdentity(trimmedIdentity) &&
    !currentUserEmail;

  const { data: resolvedEmail, isLoading } = useQuery({
    queryKey: ["user-by-auth-id", trimmedIdentity],
    queryFn: async () => {
      try {
        const res = await agent.Users.queryByAuthID(trimmedIdentity);
        return res.success && res.data?.email ? res.data.email : null;
      } catch {
        return null;
      }
    },
    enabled: shouldResolve,
    staleTime: AUTH_ID_EMAIL_QUERY_STALE_TIME_MS,
  });

  const label = isEmailIdentity(trimmedIdentity)
    ? trimmedIdentity
    : (currentUserEmail ??
      resolvedEmail ??
      (isLoading ? "Resolving..." : "Unknown user"));

  return <>{label}</>;
}

function AuthorizedIdentitiesRow({
  identities,
  onMoreClick,
}: {
  identities: string[];
  onMoreClick?: () => void;
}) {
  const visibleIdentities = getVisibleOverflowItems(
    identities,
    MAX_AUTHORIZED_IDENTITIES_IN_ROW,
  );
  const hiddenCount = identities.length - visibleIdentities.length;

  return (
    <>
      <span className="min-w-0 text-xs text-gray-600 dark:text-zinc-300">
        {visibleIdentities.map((identity, index) => (
          <React.Fragment key={identity}>
            <AuthorizedIdentityText identity={identity} />
            {index < visibleIdentities.length - 1 ? ", " : ""}
          </React.Fragment>
        ))}
      </span>
      {hiddenCount > 0 && onMoreClick && (
        <button
          type="button"
          onClick={onMoreClick}
          aria-label={`Show ${hiddenCount} more authorized users`}
          className="ml-1 inline border-0 bg-transparent p-0 align-baseline text-xs font-medium text-highlight underline-offset-2 hover:text-highlightDarker hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight"
        >
          + {hiddenCount} more
        </button>
      )}
      {hiddenCount > 0 && !onMoreClick && (
        <span className="ml-1 inline text-xs font-medium text-highlight">
          + {hiddenCount} more
        </span>
      )}
    </>
  );
}

function isPendingDisplay(port: DisplayPort): boolean {
  return port.isEndpointPending === true;
}

function getEndpointDisplay(port: DisplayPort): string | null {
  if (isPendingDisplay(port)) return "pending";
  return port.hostname ? `https://${port.hostname}` : null;
}

function publicPortLabel(port: DisplayPort): string {
  if (isPendingDisplay(port)) return "pending";
  return String(port.portNumber);
}

function PendingValue({ children }: { children: React.ReactNode }) {
  return (
    <span className="font-mono italic text-gray-400 dark:text-zinc-500">
      {children}
    </span>
  );
}

function EditableListRow({
  value,
  onRemove,
  disabled = false,
  isRemoving = false,
}: {
  value: string;
  onRemove: () => void;
  disabled?: boolean;
  isRemoving?: boolean;
}) {
  return (
    <div className="flex h-10 items-center justify-between rounded px-1.5 text-sm text-neutral-300 hover:bg-gray-100 dark:hover:bg-zinc-800">
      <span className="min-w-0 truncate">
        <AuthorizedIdentityText identity={value} />
      </span>
      <Button
        variant="secondary"
        onClick={onRemove}
        disabled={disabled}
        isLoading={isRemoving}
        loadingText="Removing..."
        className="ml-3 h-7 shrink-0 text-xs"
      >
        Remove
      </Button>
    </div>
  );
}

interface SecureLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (
    portNumber: number,
    httpProtocol: HttpPortProtocol,
    customHostname: string,
    authorizedEmails: string[],
    allowPublicUnauthenticated: boolean,
  ) => Promise<void>;
  existingPorts: DisplayPort[];
}

const SecureLinkDialog: React.FC<SecureLinkDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  existingPorts,
}) => {
  const { me } = useContext(UserContext);
  const [portNumber, setPortNumber] = useState("");
  const [customHostname, setCustomHostname] = useState("");
  const [hostnameError, setHostnameError] = useState("");
  const [authorizedEmails, setAuthorizedEmails] = useState<string[]>([]);
  const [emailInput, setEmailInput] = useState("");
  const [allowPublic, setAllowPublic] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPortNumber("");
    setCustomHostname("");
    setHostnameError("");
    setAuthorizedEmails(me?.email ? [me.email] : []);
    setAllowPublic(false);
    setEmailInput("");
  }, [me?.email, open]);

  const handleAddEmail = () => {
    const trimmed = emailInput.trim();
    if (!trimmed || authorizedEmails.includes(trimmed)) return;
    setAuthorizedEmails((prev) => [...prev, trimmed]);
    setEmailInput("");
  };

  const handleRemoveEmail = (email: string) => {
    setAuthorizedEmails((prev) => prev.filter((e) => e !== email));
  };

  const portNum = parseInt(portNumber, 10);
  const isValidRange = !isNaN(portNum) && portNum > 0 && portNum <= 65535;
  const alreadyAllocated =
    isValidRange && existingPorts.some((p) => p.portNumber === portNum);
  const isValid = isValidRange && !alreadyAllocated && !hostnameError;

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      await onSubmit(
        portNum,
        HttpPortProtocol.HTTP,
        customHostname.trim(),
        allowPublic ? [] : authorizedEmails,
        allowPublic,
      );
      onOpenChange(false);
    } catch (error) {
      if (isSecureLinkNameConflictError(error)) {
        setHostnameError(SECURE_LINK_NAME_CONFLICT_MESSAGE);
        return;
      }
      throw error;
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) handleClose();
      }}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-white">Create Secure Link</DialogTitle>
          <DialogDescription className="text-neutral-400">
            Expose an HTTP service protected with your login.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-neutral-400">Port number</label>
            <Input
              type="number"
              placeholder="8080"
              value={portNumber}
              onChange={(e) => setPortNumber(e.target.value)}
              min={1}
              max={65535}
            />
            {alreadyAllocated && (
              <p className="text-xs text-red-400">
                Port {portNum} is already open on this node.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-neutral-400">
              Custom hostname{" "}
              <span className="text-neutral-600">(optional)</span>
            </label>
            <Input
              placeholder="my-service"
              value={customHostname}
              hasError={!!hostnameError}
              onChange={(e) => {
                setCustomHostname(e.target.value);
                setHostnameError(
                  e.target.value ? validateTunnelName(e.target.value) : "",
                );
              }}
            />
            {hostnameError ? (
              <p className="text-xs text-red-400">{hostnameError}</p>
            ) : (
              <p className="text-xs text-neutral-500">
                Lowercase letters, digits, and hyphens only. Must start and end
                with a letter or digit.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <label className="text-base font-semibold text-neutral-200">
              Share Access
            </label>
            <label className="flex items-center gap-3 cursor-pointer select-none py-1 pl-1">
              <input
                type="checkbox"
                checked={allowPublic}
                onChange={(e) => setAllowPublic(e.target.checked)}
                className="h-4 w-4 shrink-0 rounded border-neutral-700 bg-neutral-900"
                style={{ accentColor: "var(--color-highlight)" }}
              />
              <span className="text-sm text-neutral-300">
                Disable authentication
              </span>
            </label>
            {allowPublic && (
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 flex flex-row items-start text-amber-500">
                <div className="ml-1">
                  <div className="flex flex-row items-center">
                    <ExclamationTriangleIcon className="h-5 w-5 mr-2 flex-shrink-0" />
                    <p className="text-sm font-bold">
                      Disable authentication on this secure link?
                    </p>
                  </div>
                  <p className="text-sm text-gray-300 mt-2">
                    Sign-in will be entirely removed. Anyone with the link can
                    access this service.
                  </p>
                </div>
              </div>
            )}
            {!allowPublic && (
              <>
                <p className="text-xs text-neutral-500 -mt-1">
                  Grant access to specific users by adding their email.
                </p>
                <div className="flex flex-row gap-2">
                  <Input
                    placeholder="user@example.com"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleAddEmail();
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    variant="secondary"
                    onClick={handleAddEmail}
                    disabled={!emailInput.trim()}
                  >
                    Add User
                  </Button>
                </div>
                <div>
                  {authorizedEmails.map((email) => (
                    <EditableListRow
                      key={email}
                      value={email}
                      onRemove={() => handleRemoveEmail(email)}
                    />
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isValid}
            isLoading={isSubmitting}
            loadingText="Creating..."
          >
            Create Secure Link
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const EditDestinationPortDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  port: DisplayPort;
  onSubmit: (portNumber: number) => Promise<void>;
}> = ({ open, onOpenChange, port, onSubmit }) => {
  const [portNumber, setPortNumber] = useState(
    String(port.serverPort || port.portNumber),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPortNumber(String(port.serverPort || port.portNumber));
  }, [open, port.portNumber, port.serverPort]);

  const portNum = parseInt(portNumber, 10);
  const isValidRange = !isNaN(portNum) && portNum > 0 && portNum <= 65535;
  const isValid = isValidRange;

  const handleSubmit = async () => {
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      await onSubmit(portNum);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onOpenChange(false);
      }}
    >
      <ScrollableNetworkDialogContent>
        <DialogHeader className="pb-2">
          <DialogTitle className="text-white">
            Edit Destination Port
          </DialogTitle>
          <DialogDescription className="text-neutral-400">
            Update the internal server port this secure link forwards to.
          </DialogDescription>
        </DialogHeader>
        <ScrollableNetworkDialogBody>
          <div className="flex flex-col gap-1.5">
            <label className="text-sm text-neutral-400">Destination port</label>
            <Input
              type="number"
              placeholder="8080"
              value={portNumber}
              onChange={(e) => setPortNumber(e.target.value)}
              min={1}
              max={65535}
            />
            <p className="text-xs text-neutral-500">
              Update the internal server port this secure link forwards to.
            </p>
          </div>
        </ScrollableNetworkDialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isValid}
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            Save Changes
          </Button>
        </DialogFooter>
      </ScrollableNetworkDialogContent>
    </Dialog>
  );
};

const EditIPRestrictionsDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  port: DisplayPort;
  onSubmit: (allowedSources: string[]) => Promise<void>;
}> = ({ open, onOpenChange, port, onSubmit }) => {
  const [allowedSources, setAllowedSources] = useState<string[]>(
    port.allowedSources || [],
  );
  const [sourceInput, setSourceInput] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setAllowedSources(port.allowedSources || []);
    setSourceInput("");
  }, [open, port.allowedSources]);

  const handleAddSource = () => {
    const trimmed = sourceInput.trim();
    if (!trimmed || allowedSources.includes(trimmed)) return;
    setAllowedSources((prev) => [...prev, trimmed]);
    setSourceInput("");
  };

  const handleRemoveSource = (source: string) => {
    setAllowedSources((prev) => prev.filter((item) => item !== source));
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(allowedSources);
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onOpenChange(false);
      }}
    >
      <ScrollableNetworkDialogContent>
        <DialogHeader className="pb-2">
          <DialogTitle className="text-white">Edit IP Restrictions</DialogTitle>
          <DialogDescription className="text-neutral-400">
            Control which source IP addresses can reach this secure link.
          </DialogDescription>
        </DialogHeader>
        <ScrollableNetworkDialogBody>
          <AllowedSourcesEditor
            allowedSources={allowedSources}
            sourceInput={sourceInput}
            onSourceInputChange={setSourceInput}
            onAddSource={handleAddSource}
            onRemoveSource={handleRemoveSource}
          />
        </ScrollableNetworkDialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            loadingText="Saving..."
          >
            Save Changes
          </Button>
        </DialogFooter>
      </ScrollableNetworkDialogContent>
    </Dialog>
  );
};

export const ShareSecureLinkDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  port: DisplayPort;
  onSubmit: (authorizedEmails: string[], allowPublic: boolean) => Promise<void>;
}> = ({ open, onOpenChange, port, onSubmit }) => {
  const { me } = useContext(UserContext);
  const [authorizedEmails, setAuthorizedEmails] = useState<string[]>(
    port.authorizedEmails ?? [],
  );
  const [emailInput, setEmailInput] = useState("");
  const [allowPublic, setAllowPublic] = useState(
    port.allowPublicUnauthenticated ?? false,
  );
  const [pendingAllowPublic, setPendingAllowPublic] = useState<boolean | null>(
    null,
  );
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [view, setView] = useState<"access" | "qr">("access");
  const isSubmitting = pendingAction !== null;
  const displayAllowPublic = pendingAllowPublic ?? allowPublic;
  const hasPendingAuthorizationChange =
    pendingAllowPublic !== null && pendingAllowPublic !== allowPublic;
  const pendingDisableAuthentication =
    hasPendingAuthorizationChange && pendingAllowPublic === true;
  const pendingEnableAuthentication =
    hasPendingAuthorizationChange && pendingAllowPublic === false;
  const userAccessEditingDisabled =
    isSubmitting || hasPendingAuthorizationChange;
  const secureLinkUrl = port.hostname ? `https://${port.hostname}` : "";
  const currentUserIdentities = getCurrentUserAccessIdentities(me);
  const currentUserCanAccess = userCanAccessSecureLink(
    {
      allowPublicUnauthenticated: allowPublic,
      authorizedEmails,
    },
    currentUserIdentities,
  );
  const linkActionsDisabled = !secureLinkUrl || !currentUserCanAccess;
  const linkActionsTooltipText =
    secureLinkUrl && !currentUserCanAccess
      ? "You do not have access to this secure link, so copying the link and QR code are disabled."
      : "";

  useEffect(() => {
    if (!open) return;
    const initial = port.allowPublicUnauthenticated ?? false;
    setAuthorizedEmails(port.authorizedEmails ?? []);
    setEmailInput("");
    setAllowPublic(initial);
    setPendingAllowPublic(null);
    setPendingAction(null);
    setView("access");
  }, [open, port.authorizedEmails, port.allowPublicUnauthenticated]);

  const persistAccess = async (
    nextAuthorizedEmails: string[],
    nextAllowPublic: boolean,
    action: string,
  ) => {
    setPendingAction(action);
    try {
      await onSubmit(
        nextAllowPublic ? [] : nextAuthorizedEmails,
        nextAllowPublic,
      );
      setAuthorizedEmails(nextAllowPublic ? [] : nextAuthorizedEmails);
      setAllowPublic(nextAllowPublic);
      setPendingAllowPublic(null);
      return true;
    } catch {
      toast.error("Unable to update secure link access.");
      return false;
    } finally {
      setPendingAction(null);
    }
  };

  const handleAddEmail = async () => {
    const trimmed = emailInput.trim();
    if (
      !trimmed ||
      authorizedEmails.includes(trimmed) ||
      hasPendingAuthorizationChange
    ) {
      return;
    }
    const nextAuthorizedEmails = [...authorizedEmails, trimmed];
    const updated = await persistAccess(
      nextAuthorizedEmails,
      false,
      `add:${trimmed}`,
    );
    if (updated) setEmailInput("");
  };

  const handleRemoveEmail = async (email: string) => {
    if (hasPendingAuthorizationChange) return;
    const nextAuthorizedEmails = authorizedEmails.filter(
      (item) => item !== email,
    );
    await persistAccess(nextAuthorizedEmails, false, `remove:${email}`);
  };

  const handleAllowPublicChange = (nextAllowPublic: boolean) => {
    if (nextAllowPublic === allowPublic) {
      setPendingAllowPublic(null);
      return;
    }
    setPendingAllowPublic(nextAllowPublic);
  };

  const handleConfirmAllowPublicChange = async () => {
    if (pendingAllowPublic === null || pendingAllowPublic === allowPublic) {
      return;
    }
    await persistAccess(
      pendingAllowPublic ? [] : authorizedEmails,
      pendingAllowPublic,
      "authorization",
    );
  };

  const handleCopyLink = async () => {
    if (linkActionsDisabled) return;
    await navigator.clipboard.writeText(secureLinkUrl);
    toast.success("Link copied to the clipboard.");
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && !isSubmitting) onOpenChange(false);
      }}
    >
      <ScrollableNetworkDialogContent className="sm:max-w-[520px]">
        {view === "qr" ? (
          <>
            <DialogHeader className="pb-2">
              <button
                type="button"
                onClick={() => setView("access")}
                className="mb-1 inline-flex w-fit items-center gap-2 rounded-md px-1 py-1 text-sm text-neutral-300 transition hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight"
              >
                <ArrowLeft className="h-4 w-4 text-neutral-300" />
                Back
              </button>
              <DialogTitle className="text-white">Scan QR code</DialogTitle>
              <DialogDescription className="text-neutral-400">
                Open this secure link from another device.
              </DialogDescription>
            </DialogHeader>
            <ScrollableNetworkDialogBody className="items-center pr-0">
              {secureLinkUrl ? (
                <div className="rounded-xl border border-zinc-800 bg-white p-4 shadow-sm">
                  <QRCodeSVG
                    role="img"
                    aria-label="QR code for secure link"
                    value={secureLinkUrl}
                    size={224}
                    level="M"
                    marginSize={4}
                    bgColor="#fff"
                    fgColor="#000"
                    className="h-56 w-56"
                    shapeRendering="crispEdges"
                  />
                </div>
              ) : (
                <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-200">
                  Secure link pending.
                </div>
              )}
              <p className="max-w-full break-all text-center font-mono text-xs text-neutral-400">
                {secureLinkUrl || "Secure link pending"}
              </p>
            </ScrollableNetworkDialogBody>
            <DialogFooter>
              <Button variant="primary" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader className="pb-2">
              <DialogTitle className="text-white">
                Share Secure Link
              </DialogTitle>
              <DialogDescription className="text-neutral-400">
                Choose who can access this secure link.
              </DialogDescription>
            </DialogHeader>
            <ScrollableNetworkDialogBody>
              <div className="flex min-h-0 flex-col gap-3">
                <label className="text-base font-semibold text-neutral-200">
                  Authorization
                </label>
                <label className="flex cursor-pointer select-none items-center gap-3 py-1 pl-1">
                  <input
                    type="checkbox"
                    checked={displayAllowPublic}
                    disabled={isSubmitting}
                    onChange={(event) =>
                      handleAllowPublicChange(event.target.checked)
                    }
                    className="h-4 w-4 shrink-0 rounded border-neutral-700 bg-neutral-900 disabled:cursor-not-allowed disabled:opacity-50"
                    style={{ accentColor: "var(--color-highlight)" }}
                  />
                  <span className="text-sm text-neutral-300">
                    Disable authentication
                  </span>
                </label>
                {pendingDisableAuthentication && (
                  <div className="flex flex-row items-start rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-amber-500">
                    <div className="ml-1">
                      <div className="flex flex-row items-center">
                        <ExclamationTriangleIcon className="mr-2 h-5 w-5 flex-shrink-0" />
                        <p className="text-sm font-bold">
                          Disable authentication on this secure link?
                        </p>
                      </div>
                      <p className="mt-2 text-sm text-gray-300">
                        Sign-in will be entirely removed. Anyone with the link
                        can access this service.
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => void handleConfirmAllowPublicChange()}
                        disabled={isSubmitting}
                        isLoading={pendingAction === "authorization"}
                        loadingText="Disabling..."
                        className="mt-3 border-amber-500/50 bg-amber-500 !text-black hover:bg-amber-400 disabled:bg-amber-500/50 disabled:!text-black/70 dark:!text-black"
                      >
                        Yes, disable authentication
                      </Button>
                    </div>
                  </div>
                )}
                {pendingEnableAuthentication && (
                  <div className="flex flex-row items-start rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-amber-500">
                    <div className="ml-1">
                      <div className="flex flex-row items-center">
                        <ExclamationTriangleIcon className="mr-2 h-5 w-5 flex-shrink-0" />
                        <p className="text-sm font-bold">
                          Enable authentication on this secure link?
                        </p>
                      </div>
                      <p className="mt-2 text-sm text-gray-300">
                        Authentication will be required. When users are added,
                        only users granted explicit permission to this link will
                        be able to access it. If no users are added, any
                        authenticated user can access this link.
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => void handleConfirmAllowPublicChange()}
                        disabled={isSubmitting}
                        isLoading={pendingAction === "authorization"}
                        loadingText="Enabling..."
                        className="mt-3 border-amber-500/50 bg-amber-500 !text-black hover:bg-amber-400 disabled:bg-amber-500/50 disabled:!text-black/70 dark:!text-black"
                      >
                        Yes, enable authentication
                      </Button>
                    </div>
                  </div>
                )}
                {!displayAllowPublic && (
                  <>
                    <p className="text-xs text-neutral-500">
                      Add user emails to grant access. Users are not notified
                      they&apos;ve been granted access.
                    </p>
                    <div className="flex flex-row gap-2">
                      <Input
                        placeholder="user@example.com"
                        value={emailInput}
                        onChange={(event) => setEmailInput(event.target.value)}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            event.preventDefault();
                            void handleAddEmail();
                          }
                        }}
                        disabled={userAccessEditingDisabled}
                        className="flex-1"
                      />
                      <Button
                        variant="secondary"
                        onClick={() => void handleAddEmail()}
                        disabled={
                          !emailInput.trim() || userAccessEditingDisabled
                        }
                        isLoading={pendingAction === `add:${emailInput.trim()}`}
                        loadingText="Adding..."
                      >
                        Add User
                      </Button>
                    </div>
                    <div className="min-h-[150px] max-h-[min(16rem,40vh)] overflow-y-auto pr-2">
                      {authorizedEmails.length > 0 ? (
                        authorizedEmails.map((email) => (
                          <EditableListRow
                            key={email}
                            value={email}
                            disabled={userAccessEditingDisabled}
                            isRemoving={pendingAction === `remove:${email}`}
                            onRemove={() => void handleRemoveEmail(email)}
                          />
                        ))
                      ) : (
                        <div className="rounded-md border border-zinc-800 bg-white/[.02] px-3 py-2 text-xs leading-5 text-neutral-400">
                          No explicit users have access. Any authenticated user
                          can access this link.
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </ScrollableNetworkDialogBody>
            <DialogFooter className="flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 flex-wrap items-center gap-2">
                <DisabledActionTooltip message={linkActionsTooltipText}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => void handleCopyLink()}
                    disabled={linkActionsDisabled}
                    className="gap-2"
                  >
                    <Copy className="h-4 w-4" />
                    Copy link
                  </Button>
                </DisabledActionTooltip>
                <DisabledActionTooltip message={linkActionsTooltipText}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setView("qr")}
                    disabled={linkActionsDisabled}
                    className="gap-2"
                  >
                    <QrCode className="h-4 w-4" />
                    Scan QR code
                  </Button>
                </DisabledActionTooltip>
              </div>
              <Button
                variant="primary"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Done
              </Button>
            </DialogFooter>
          </>
        )}
      </ScrollableNetworkDialogContent>
    </Dialog>
  );
};

export interface NodeSecureLinksSectionProps {
  ports: DisplayPort[];
  isLoading?: boolean;
  canOpenSecureLink?: boolean;
  canManagePort?: (port: DisplayPort) => boolean;
  onOpenHTTPPort: (
    portNumber: number,
    options: OpenHTTPPortOptions,
  ) => Promise<void>;
  onSetPortTarget: (portId: string, portNumber: number) => Promise<void>;
  onSetPortAllowedSources: (
    portId: string,
    allowedSources: string[],
  ) => Promise<void>;
  onSetHTTPPortAccess: (
    portId: string,
    authorizedEmails: string[],
    allowPublicUnauthenticated: boolean,
  ) => Promise<void>;
  onClosePort: (portId: string) => Promise<void>;
  showMicroK8sNodePortNotice?: boolean;
}

export const NodeSecureLinksSection: React.FC<NodeSecureLinksSectionProps> = ({
  ports,
  isLoading = false,
  canOpenSecureLink = true,
  canManagePort = () => true,
  onOpenHTTPPort,
  onSetPortTarget,
  onSetPortAllowedSources,
  onSetHTTPPortAccess,
  onClosePort,
  showMicroK8sNodePortNotice = false,
}) => {
  const { activeOrgRole } = useContext(OrgContext);
  const isAdminOrMaintainer = isOrgAdminOrMaintainerRole(activeOrgRole);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDestinationPort, setEditingDestinationPort] =
    useState<DisplayPort | null>(null);
  const [editingIPRestrictionsPort, setEditingIPRestrictionsPort] =
    useState<DisplayPort | null>(null);
  const [sharingPort, setSharingPort] = useState<DisplayPort | null>(null);
  const [portToClose, setPortToClose] = useState<DisplayPort | null>(null);
  const columnCount = isAdminOrMaintainer ? 6 : 5;

  const handleSubmit = async (
    portNumber: number,
    httpProtocol: HttpPortProtocol,
    customHostname: string,
    authorizedEmails: string[],
    allowPublicUnauthenticated: boolean,
  ) => {
    await onOpenHTTPPort(portNumber, {
      httpProtocol,
      customHostname: customHostname || undefined,
      authorizedEmails,
      allowPublicUnauthenticated,
    });
  };

  const handleEditDestinationPort = async (portNumber: number) => {
    const currentPort = editingDestinationPort!;
    if ((currentPort.serverPort || currentPort.portNumber) === portNumber)
      return;

    await onSetPortTarget(currentPort.portId, portNumber);
  };

  const handleEditIPRestrictions = async (allowedSources: string[]) => {
    const currentPort = editingIPRestrictionsPort!;
    if (stringArrayEquals(currentPort.allowedSources ?? [], allowedSources)) {
      return;
    }

    await onSetPortAllowedSources(currentPort.portId, allowedSources);
  };

  const handleSharePort = async (
    authorizedEmails: string[],
    allowPublic: boolean,
  ) => {
    const currentPort = sharingPort!;
    if (
      (currentPort.allowPublicUnauthenticated ?? false) === allowPublic &&
      stringArrayEquals(currentPort.authorizedEmails ?? [], authorizedEmails)
    ) {
      return;
    }

    await onSetHTTPPortAccess(
      currentPort.portId,
      authorizedEmails,
      allowPublic,
    );
  };

  return (
    <>
      <NodeDetailSectionHeader
        trailing={
          isAdminOrMaintainer ? (
            <Button
              variant="primary"
              onClick={() => {
                if (canOpenSecureLink) setDialogOpen(true);
              }}
              className="flex items-center gap-1.5"
              disabled={!canOpenSecureLink}
            >
              <Plus className="h-3.5 w-3.5 mr-1" />
              HTTP port
            </Button>
          ) : null
        }
      >
        <NodeDetailSectionDescription>
          Access any HTTP application protected with your login; share it with
          teammates or the public.
        </NodeDetailSectionDescription>
      </NodeDetailSectionHeader>

      {showMicroK8sNodePortNotice && (
        <MicroK8sNodePortNotice className="mb-4" />
      )}

      <NodeDetailTable>
        <NodeDetailTableHead>
          <tr>
            <NodeDetailTableHeaderCell className="w-full">
              Endpoint
            </NodeDetailTableHeaderCell>
            <NodeDetailTableHeaderCell className="min-w-48">
              IP Restrictions
            </NodeDetailTableHeaderCell>
            <NodeDetailTableHeaderCell>Public Port</NodeDetailTableHeaderCell>
            <NodeDetailTableHeaderCell>
              Destination Port
            </NodeDetailTableHeaderCell>
            <NodeDetailTableHeaderCell>Protocol</NodeDetailTableHeaderCell>
            {isAdminOrMaintainer && (
              <NodeDetailTableHeaderCell className="min-w-40">
                <span className="sr-only">Actions</span>
              </NodeDetailTableHeaderCell>
            )}
          </tr>
        </NodeDetailTableHead>
        <NodeDetailTableBody>
          {isLoading ? (
            <NetworkTableLoadingRow
              colSpan={columnCount}
              label="Loading secure links..."
            />
          ) : ports.length === 0 ? (
            <tr>
              <NodeDetailTableCell
                colSpan={columnCount}
                className="py-6 text-center text-gray-500 dark:text-secondary"
              >
                No HTTP services are exposed on this node.
              </NodeDetailTableCell>
            </tr>
          ) : (
            ports.map((port) => {
              const endpoint = getEndpointDisplay(port);
              const isPending = isPendingDisplay(port);
              const canEditPort = isAdminOrMaintainer && canManagePort(port);
              return (
                <tr
                  key={port.portId}
                  className="group"
                  data-secure-link-hostname={port.hostname}
                >
                  <NodeDetailTableCell className="w-full">
                    {isPending && endpoint ? (
                      <PendingValue>{endpoint}</PendingValue>
                    ) : endpoint ? (
                      <span className="inline-flex max-w-full items-center gap-1 font-mono">
                        <a
                          href={endpoint}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="block max-w-full truncate underline text-highlight hover:text-highlightLighter"
                        >
                          {endpoint}
                        </a>
                        <CopyButton value={endpoint} />
                      </span>
                    ) : (
                      <span className="text-gray-500 dark:text-secondary">
                        —
                      </span>
                    )}
                    <div className="mt-2">
                      <div className="text-xs leading-6">
                        <span className="mr-2 font-medium text-gray-400 dark:text-zinc-500">
                          Authorization
                        </span>
                        {port.allowPublicUnauthenticated ? (
                          <NeutralInlinePill>Public</NeutralInlinePill>
                        ) : (port.authorizedEmails ?? []).length > 0 ? (
                          <AuthorizedIdentitiesRow
                            identities={port.authorizedEmails ?? []}
                            onMoreClick={
                              canEditPort
                                ? () => setSharingPort(port)
                                : undefined
                            }
                          />
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-zinc-500">
                            Any authenticated user
                          </span>
                        )}
                      </div>
                    </div>
                  </NodeDetailTableCell>
                  <NodeDetailTableCell className="min-w-48">
                    <div className="flex items-start gap-1">
                      <AllowedSourcesValue
                        allowedSources={port.allowedSources ?? []}
                        maxVisible={MAX_ALLOWED_SOURCES_IN_ROW}
                        onMoreClick={
                          canEditPort
                            ? () => setEditingIPRestrictionsPort(port)
                            : undefined
                        }
                      />
                      {canEditPort && (
                        <NodeDetailIconAction
                          className="shrink-0"
                          onClick={() => setEditingIPRestrictionsPort(port)}
                          aria-label="Edit IP restrictions"
                          title="Edit IP restrictions"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </NodeDetailIconAction>
                      )}
                    </div>
                  </NodeDetailTableCell>
                  <NodeDetailTableCell className="font-mono text-gray-900 dark:text-white">
                    {isPending ? (
                      <PendingValue>{publicPortLabel(port)}</PendingValue>
                    ) : (
                      publicPortLabel(port)
                    )}
                  </NodeDetailTableCell>
                  <NodeDetailTableCell className="font-mono text-gray-900 dark:text-white">
                    <span className="inline-flex items-center gap-1">
                      {port.serverPort || port.portNumber}
                      {canEditPort && (
                        <NodeDetailIconAction
                          onClick={() => setEditingDestinationPort(port)}
                          aria-label="Edit destination port"
                          title="Edit destination port"
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </NodeDetailIconAction>
                      )}
                    </span>
                  </NodeDetailTableCell>
                  <NodeDetailTableCell className="text-gray-500 dark:text-secondary">
                    {httpProtocolLabel(port.httpProtocol)}
                  </NodeDetailTableCell>
                  {isAdminOrMaintainer && (
                    <NodeDetailTableCell align="right">
                      {canEditPort && (
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="primary"
                            onClick={() => setSharingPort(port)}
                            data-secure-link-share-hostname={port.hostname}
                            className="h-8 px-3"
                          >
                            Share
                          </Button>
                          <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild>
                              <NodeDetailIconAction aria-label="More secure link actions">
                                <MoreHorizontal className="h-5 w-5" />
                              </NodeDetailIconAction>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent side="bottom" align="end">
                              <DropdownMenuItem
                                className="text-red-400 focus:text-red-400"
                                onClick={() => setPortToClose(port)}
                              >
                                Remove
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                    </NodeDetailTableCell>
                  )}
                </tr>
              );
            })
          )}
        </NodeDetailTableBody>
      </NodeDetailTable>

      <SecureLinkDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSubmit={handleSubmit}
        existingPorts={ports}
      />
      {editingDestinationPort && (
        <EditDestinationPortDialog
          open={editingDestinationPort !== null}
          onOpenChange={(open) => {
            if (!open) setEditingDestinationPort(null);
          }}
          port={editingDestinationPort}
          onSubmit={handleEditDestinationPort}
        />
      )}
      {editingIPRestrictionsPort && (
        <EditIPRestrictionsDialog
          open={editingIPRestrictionsPort !== null}
          onOpenChange={(open) => {
            if (!open) setEditingIPRestrictionsPort(null);
          }}
          port={editingIPRestrictionsPort}
          onSubmit={handleEditIPRestrictions}
        />
      )}
      {sharingPort && (
        <ShareSecureLinkDialog
          open={sharingPort !== null}
          onOpenChange={(open) => {
            if (!open) setSharingPort(null);
          }}
          port={sharingPort}
          onSubmit={handleSharePort}
        />
      )}
      <ConfirmDialog
        open={portToClose !== null}
        onOpenChange={(open) => {
          if (!open) setPortToClose(null);
        }}
        title="Remove Secure Link"
        description="This secure link will be permanently removed from this node and the endpoint will no longer be available. This action cannot be undone."
        confirmLabel="Remove Secure Link"
        confirmLoadingLabel="Removing Secure Link..."
        details={[
          {
            label: "Endpoint",
            value: portToClose?.hostname
              ? `https://${portToClose.hostname}`
              : "—",
          },
          {
            label: "Public Port",
            value: String(portToClose?.portNumber ?? "—"),
          },
          {
            label: "Destination Port",
            value: String(
              portToClose?.serverPort || portToClose?.portNumber || "—",
            ),
          },
          {
            label: "Protocol",
            value: portToClose
              ? httpProtocolLabel(portToClose.httpProtocol)
              : "—",
          },
        ]}
        onConfirm={() => onClosePort(portToClose!.portId)}
      />
    </>
  );
};
